import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PractitionerNotePreferences, PractitionerSettings } from 'src/models';
import { ambientApi } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';

type UpdatePractitionerSettingsParams = {
  practitioner_id?: number;
  settings: Partial<
    Omit<
      PractitionerSettings,
      | 'created_at'
      | 'created_by'
      | 'last_modified'
      | 'last_modified_by'
      | 'ambient_version'
      | 'settings'
      | 'note_preferences'
    >
  > &
    Partial<PractitionerNotePreferences>;
};

export const useUpdatePractitionerSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['updatePractitionerSettings'],
    mutationFn: (params: UpdatePractitionerSettingsParams) => {
      const { practitioner_id, settings } = params;
      const body = practitioner_id
        ? { practitioner_id, ...settings }
        : settings;
      return ambientApi
        .post<PractitionerSettings>('/practitioners/settings', body)
        .then((res) => res.data);
    },
    onError: () => {
      showToastError('Failed to update settings');
    },
    onSuccess: (_, variables) => {
      if (variables.practitioner_id) {
        showToastSuccess('Practitioner settings updated successfully');
        queryClient.invalidateQueries({
          queryKey: ['practitionerSettings', variables.practitioner_id],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ['practitionerSettings'],
        });
      }
    },
  });
};
